"use client";

import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Navbar from "../../../components/navbar";
import Footer from "../../../components/footer";
import Button from "../../../components/Button";
import FormInput from "../../../components/FormInput";
import Dropdown from "../../../components/Dropdown";
import { Trash2 } from "lucide-react";
import styles from "../css/creditFinancial.module.css";

const CreditFinancial = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const creditFlow = location?.state?.creditFlow;
  const isReviewMode = creditFlow?.mode === "review";
  const savedData = location?.state?.formData || {};
  const creditStatus = String(creditFlow?.status || "").toLowerCase();
  const isReadOnly = isReviewMode && ["awaiting", "pending", "decision"].includes(creditStatus);

  const steps = useMemo(
    () => [
      { key: "personal", label: "Personal" },
      { key: "contact", label: "Contact" },
      { key: "employment", label: "Employment" },
      { key: "financial", label: "Financial" },
      { key: "consent", label: "Consent" },
    ],
    []
  );

  const paymentMethods = useMemo(() => ["EFT", "Card", "Debit order"], []);

  const [hasExistingCredit, setHasExistingCredit] = useState(Boolean(savedData.hasExistingCredit));
  const [preferredPaymentMethod, setPreferredPaymentMethod] = useState(
    savedData.preferredPaymentMethod || ""
  );

  const [expenses, setExpenses] = useState(() => {
    const e = Array.isArray(savedData.expenses) ? savedData.expenses : [];
    if (e.length > 0) return e;
    return [
      { name: "", amount: "" },
      { name: "", amount: "" },
    ];
  });

  const updateExpense = (idx, key, value) => {
    if (isReadOnly) return;
    setExpenses((prev) =>
      prev.map((row, i) => (i === idx ? { ...row, [key]: value } : row))
    );
  };

  const addExpenseRowAfter = (idx) => {
    if (isReadOnly) return;
    setExpenses((prev) => {
      const next = [...prev];
      next.splice(idx + 1, 0, { name: "", amount: "" });
      return next;
    });
  };

  const removeExpenseRow = (idx) => {
    if (isReadOnly) return;
    setExpenses((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((_, i) => i !== idx);
    });
  };

  return (
    <div className={styles.page}>
      <Navbar backTo="/" hideBack={isReviewMode} />

      <main className={styles.main}>
        <div className={styles.titleWrap}>
          <h1 className={styles.title}>Credit Application</h1>
          <p className={styles.subtitle}>
            Complete the form below to apply for purchase-on-credit at your pharmacy.
          </p>
        </div>

        <div className={styles.stepper} aria-label="Application steps">
          {steps.map((s, idx) => {
            const isActive = idx === 3;
            const isDone = idx < 3;
            return (
              <div key={s.key} className={styles.stepItem}>
                <div
                  className={`${styles.stepCircle} ${isActive ? styles.stepActive : ""} ${
                    isDone ? styles.stepDone : ""
                  }`}
                >
                  {isDone ? "✓" : idx + 1}
                </div>
                <div className={styles.stepLabel}>{s.label}</div>
                {idx < steps.length - 1 ? <div className={styles.stepLine} aria-hidden /> : null}
              </div>
            );
          })}
        </div>

        <section className={styles.card}>
          <div className={styles.sectionHeader}>
            <div className={styles.sectionIcon} aria-hidden>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M4 7h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                <path d="M6 3h12v18H6V3z" stroke="currentColor" strokeWidth="2" />
              </svg>
            </div>
            <div className={styles.sectionTitle}>Basic Financial Info</div>
          </div>

          <div className={styles.toggleRow}>
            <div className={styles.toggleLabel}>Do you have existing credit?</div>
            <label className={styles.switch}>
              <input
                type="checkbox"
                checked={hasExistingCredit}
                onChange={(e) => {
                  if (isReadOnly) return;
                  setHasExistingCredit(e.target.checked);
                }}
                disabled={isReadOnly}
              />
              <span className={styles.slider} />
            </label>
          </div>

          <div className={styles.blockLabel}>Estimated Monthly Expenses</div>

          <div className={styles.expensesWrap}>
            {expenses.map((row, idx) => (
              <div key={idx} className={styles.expenseRow}>
                <FormInput
                  label=""
                  name={`expenseName_${idx}`}
                  value={row.name}
                  onChange={(e) => updateExpense(idx, "name", e.target.value)}
                  placeholder="Expense"
                  disabled={isReadOnly}
                />

                <FormInput
                  label=""
                  name={`expenseAmount_${idx}`}
                  type="number"
                  value={row.amount}
                  onChange={(e) => updateExpense(idx, "amount", e.target.value)}
                  placeholder="0.00"
                  prefix="R"
                  min={0}
                  disabled={isReadOnly}
                />

                <div className={styles.rowActions}>
                  {idx > 0 ? (
                    <Button
                      variant="secondary"
                      type="button"
                      className={styles.minusBtn}
                      onClick={() => removeExpenseRow(idx)}
                      title="Remove expense"
                      ariaLabel="Remove expense"
                      disabled={isReadOnly}
                    >
                      <Trash2 size={18} color="#ffffff" strokeWidth={2} />
                    </Button>
                  ) : (
                    <div className={styles.actionSpacer} />
                  )}

                  {idx === expenses.length - 1 ? (
                    <Button
                      variant="primary"
                      type="button"
                      className={styles.plusBtn}
                      onClick={() => addExpenseRowAfter(idx)}
                      title="Add expense"
                      ariaLabel="Add expense"
                      disabled={isReadOnly}
                    >
                      +
                    </Button>
                  ) : (
                    <div className={styles.actionSpacer} />
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className={styles.paymentRow}>
            <Dropdown
              label="Preferred Payment Method"
              name="preferredPaymentMethod"
              options={paymentMethods}
              value={preferredPaymentMethod}
              onChange={(v) => {
                if (isReadOnly) return;
                setPreferredPaymentMethod(v);
              }}
              placeholder="Select method"
              disabled={isReadOnly}
            />
          </div>

          <div className={styles.divider} />

          <div className={styles.actions}>
            <Button
              variant="primary"
              type="button"
              onClick={() =>
                isReviewMode
                  ? navigate("/credits/employment", {
                      state: {
                        creditFlow,
                        formData: {
                          ...savedData,
                          hasExistingCredit,
                          preferredPaymentMethod,
                          expenses,
                        },
                      },
                    })
                  : navigate(-1)
              }
            >
              Back
            </Button>
            <Button
              variant="approve"
              type="button"
              onClick={() =>
                navigate("/credits/consent", {
                  state: {
                    creditFlow,
                    formData: {
                      ...savedData,
                      hasExistingCredit,
                      preferredPaymentMethod,
                      expenses,
                    },
                  },
                })
              }
            >
              Continue
            </Button>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default CreditFinancial;
